import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import Checkbox from '@tencent/dui/lib/components/Checkbox';
import Select from '@tencent/dui/lib/components/Select';
import IProps from './more-delete-option-interface';
import styles from './more-delete-option.less';
import {
    DELETE_ANIMATION_TITLE,
    DELETE_SLIDE_RANGE_TYPE,
    DEFAULT_DELETE_ANIM_TYPE_CHECKBOX_LIST,
    DELETE_ANIM_TYPE_CHECKBOX_LIST,
    DELETE_SLIDE_RANGE_TITLE,
    DELETE_ANIMATION_TYPE_TITLE,
    DELETE_SLIDE_RANGE_LIST,
} from './constants';
import { ISlideDataModel, STTLTimeNodePresetClassType } from '@tencent/slide-sdk-model';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IAnimationEditorService } from '@src/slide/application/service/animation-editor-manager/core/interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
const MoreDeleteOption: React.FC<IProps> = (props: IProps) => {
    const {
        dialogVisible,
        currentSlide,
        slideDataList,
        selectedModel,
        selectedSlideList,
        onClose,
        getAnimationEditorService,
        showDeleteSuccessMessage,
    } = props;
    // 初始化删除动画范围
    const [slideRangeValue, setSlideRangeValue] = useState<number>(DELETE_SLIDE_RANGE_TYPE.ALL_SLIDE);
    // 初始化动画类型
    const [
        checkAnimationType,
        setCheckAnimationType,
    ] = useState<STTLTimeNodePresetClassType[]>(DEFAULT_DELETE_ANIM_TYPE_CHECKBOX_LIST);
    const animEditInstance = useRef<IAnimationEditorService>();
    useEffect(() => {
        moreDeleteOptionTdwReport();
        getAnimationEditorService()
            ?.promise()
            .then((service) => {
                animEditInstance.current = service;
            });
    }, []);

    /**
     * 切换删除页面范围下拉框
     * @param slideRange
     */
    const onChangeSlideRange = useCallback((slideRange: number) => {
        setSlideRangeValue(slideRange);
    }, []);

    /**
     * 获取复选动画类型
     * @param animationTypeArr
     */
    const onCheckboxChange = useCallback((animationTypeArr: STTLTimeNodePresetClassType[]) => {
        setCheckAnimationType(animationTypeArr);
    }, []);

    // 删除所有页动画
    const deleteAllSlidesAnimation = useCallback(() => {
        if (!animEditInstance?.current) return;
        animEditInstance.current.deleteSelectedSlideAnimation({
            deletePresetClassList: checkAnimationType,
            slideList: slideDataList,
        });
    }, [checkAnimationType, slideDataList]);

    // 删除选中元素动画
    const deleteSelectedElementAnimation = useCallback(() => {
        if (!animEditInstance?.current) return;
        const shapeModels = (selectedModel as IMultiplyModel).selectedModels || [selectedModel];
        animEditInstance.current.deleteSelectedElementAnimation({
            shapeModels,
            deletePresetClassList: checkAnimationType,
        });
    }, [checkAnimationType, selectedModel]);

    // 删除选中页动画
    const deleteSelectedSlidesAnimation = useCallback(() => {
        const selectedSlides: ISlideModel[] = selectedSlideList.length ? selectedSlideList : [currentSlide];
        const slideList = selectedSlides.map(slideModel => slideModel.sdkData);
        if (!animEditInstance?.current) return;
        animEditInstance.current.deleteSelectedSlideAnimation({
            deletePresetClassList: checkAnimationType,
            slideList: slideList as ISlideDataModel[],
        });
    }, [checkAnimationType, selectedSlideList, currentSlide]);

    const handleOk = useCallback(() => {
        const submitHandles = {
            [DELETE_SLIDE_RANGE_TYPE.ALL_SLIDE]: deleteAllSlidesAnimation,
            [DELETE_SLIDE_RANGE_TYPE.SELECTED_ELEMENT_ANIMATION]: deleteSelectedElementAnimation,
            [DELETE_SLIDE_RANGE_TYPE.SELECTED_SLIDE_ANIMATION]: deleteSelectedSlidesAnimation,
        };
        const defaultHandler = () => {};
        (submitHandles[slideRangeValue] ?? defaultHandler)();
        deleteMoreSuccessTdwReport();
        onClose();
        showDeleteSuccessMessage();
    }, [
        slideRangeValue,
        deleteAllSlidesAnimation,
        deleteSelectedElementAnimation,
        deleteSelectedSlidesAnimation,
        onClose,
    ]);

    // 批量动画-更多删除选项-tdw上报
    const moreDeleteOptionTdwReport = useCallback(() => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_BATCH,
            action: TDWActionType.DELETE_MORE,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_BATCH_DELETE_MORE,
        };
        tdw({
            ...reportInfo,
        });
    }, []);

    // 批量动画-更多删除成功-tdw上报
    const deleteMoreSuccessTdwReport = useCallback(() => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_BATCH,
            action: TDWActionType.DELETE_MORE_SUCCESS,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_BATCH_DELETE_MORE_SUCCESS,
        };
        tdw({
            ...reportInfo,
            ver5: slideRangeValue,
            ver6: checkAnimationType,
        });
    }, [slideRangeValue, checkAnimationType]);


    return (
        <div>
            <Modal
                maskClosable={true}
                title={DELETE_ANIMATION_TITLE}
                visible={dialogVisible}
                onCancel={onClose}
                onOk={handleOk}
                className={styles['batch-delete-animation']}
            >
                <p className={styles['delete-page-round-text']}>{DELETE_SLIDE_RANGE_TITLE}</p>

                <Select
                    className={styles['delete-animation-round-checkbox']}
                    defaultValue={DELETE_SLIDE_RANGE_TYPE.ALL_SLIDE}
                    onChange={onChangeSlideRange}
                    dropdownMatchSelectWidth={false}
                    listContainerClassName={styles['delete-animation-round-checkbox-list']}
                >
                    {DELETE_SLIDE_RANGE_LIST.map(item => (
                        <Select.Option value={item.value} key={item.value}>
                            {item.text}
                        </Select.Option>
                    ))}
                </Select>

                <p className={styles['delete-animation-type-text']}>{DELETE_ANIMATION_TYPE_TITLE}</p>
                <Checkbox.Group
                    className={styles['delete-animation-type-checkbox']}
                    onChange={onCheckboxChange}
                    defaultValues={checkAnimationType}
                >
                    {DELETE_ANIM_TYPE_CHECKBOX_LIST.map(item => (
                        <Checkbox
                            value={item.value}
                            key={item.value}
                            className={styles['delete-animation-type-checkbox-item']}
                        >
                            {item.text}
                        </Checkbox>
                    ))}
                </Checkbox.Group>
            </Modal>
        </div>
    );
};

export default MoreDeleteOption;
